import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ChevronDown, ChevronRight, Edit2, Trash2, Plus, GripVertical } from 'lucide-react';
import SortableQuestion from './SortableQuestion';
import QuestionInlineForm from './QuestionInlineForm';
import InlineForm from './InlineForm';

const SortableQuestionSet = ({
  questionSet,
  bandId,
  expanded,
  onToggle,
  expandedQuestions,
  setExpandedQuestions,
  handleQuestionReorder,
  handleOptionReorder,
  activeForm,
  setActiveForm,
  editingItem,
  setEditingItem,
  saveQuestion,
  saveOption,
  deleteItem,
  toggleActive,
  sensors
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: questionSet.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border-2 border-blue-200 hover:border-blue-400 transition-all">
      <div className="flex items-center gap-3">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-blue-600">
          <GripVertical size={20} />
        </div>
        
        <button onClick={onToggle} className="text-blue-600 hover:bg-blue-100 p-1 rounded transition-colors">
          {expanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
        </button>

        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h4 className="font-bold text-blue-900">{questionSet.title}</h4>
            <span className="text-xs text-gray-500">({questionSet.slug})</span>
            <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
              questionSet.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
            }`}>
              {questionSet.is_active ? 'Active' : 'Inactive'}
            </span>
          </div>
          <p className="text-xs text-gray-600 mt-0.5">{questionSet.questions?.length || 0} Questions</p>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => toggleActive('questionSet', { bandId, questionSetId: questionSet.id })}
            className="p-1.5 hover:bg-blue-100 rounded transition-colors text-sm"
          >
            {questionSet.is_active ? '👁️' : '👁️‍🗨️'}
          </button>
          <button
            onClick={() => { setEditingItem(questionSet); setActiveForm(`questionSet-${bandId}`); }}
            className="p-1.5 hover:bg-blue-100 text-blue-600 rounded transition-colors"
          >
            <Edit2 size={16} />
          </button>
          <button
            onClick={() => deleteItem('questionSet', { bandId, questionSetId: questionSet.id })}
            className="p-1.5 hover:bg-red-100 text-red-600 rounded transition-colors"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 pl-8 space-y-3">
          <div className="flex justify-between items-center">
            <h5 className="text-sm font-semibold text-gray-700">Questions</h5>
            <button
              onClick={() => { setActiveForm(`question-${bandId}-${questionSet.id}`); setEditingItem(null); }}
              className="px-3 py-1.5 bg-indigo-100 text-indigo-700 rounded-lg text-sm font-semibold hover:bg-indigo-200 transition-colors flex items-center gap-1"
            >
              <Plus size={14} /> Add Question
            </button>
          </div>

          {activeForm === `question-${bandId}-${questionSet.id}` && (
            <InlineForm
              title={editingItem ? 'Edit Question' : 'Add New Question'}
              onCancel={() => { setActiveForm(null); setEditingItem(null); }}
            >
              <QuestionInlineForm
                question={editingItem}
                questionSetId={questionSet.id}
                onSave={(data) => saveQuestion(bandId, questionSet.id, data)}
                onCancel={() => { setActiveForm(null); setEditingItem(null); }}
              />
            </InlineForm>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={(e) => handleQuestionReorder(bandId, questionSet.id, e)}
          >
            <SortableContext
              items={questionSet.questions?.map((q) => q.id) || []}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {questionSet.questions?.map((question) => (
                  <SortableQuestion
                    key={question.id}
                    question={question}
                    bandId={bandId}
                    questionSetId={questionSet.id}
                    expanded={expandedQuestions[question.id]}
                    onToggle={() => setExpandedQuestions({ ...expandedQuestions, [question.id]: !expandedQuestions[question.id] })}
                    handleOptionReorder={handleOptionReorder}
                    activeForm={activeForm}
                    setActiveForm={setActiveForm}
                    editingItem={editingItem}
                    setEditingItem={setEditingItem}
                    saveOption={saveOption}
                    deleteItem={deleteItem}
                    toggleActive={toggleActive}
                    sensors={sensors}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {(!questionSet.questions || questionSet.questions.length === 0) && activeForm !== `question-${bandId}-${questionSet.id}` && (
            <div className="text-center py-4 bg-white rounded-lg">
              <p className="text-gray-500 text-sm">No questions yet. Add your first one!</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SortableQuestionSet;